// Helper to print orders on 80mm thermal printers via the browser/system print dialog.
// Works on tablets (iPad/Android) with Bluetooth thermal printers paired as a system printer.

const RESTAURANT = {
  name: 'NOTABENE SYMPHO',
  address: 'Chaussée du Roi Baudouin 161',
  city: '7030 Mons',
  phone: '065 95 06 49',
};

const escape = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
}[c]));

const eur = (n) => Number(n || 0).toFixed(2).replace('.', ',') + ' €';

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return iso;
  }
};

const buildTicketHTML = (order) => {
  const items = order.items || [];
  const subtotal = order.subtotal ?? items.reduce((acc, it) => {
    const supTot = (it.supplements || []).reduce((s, x) => s + (x.price || 0), 0);
    return acc + (it.price + supTot) * it.quantity;
  }, 0);
  const discount = order.discount || 0;
  const total = order.total ?? (subtotal - discount);

  const itemsHTML = items.map((it) => {
    const supTot = (it.supplements || []).reduce((s, x) => s + (x.price || 0), 0);
    const linePrice = (it.price + supTot) * it.quantity;
    const giant = it.is_giant ? ' [GEANTE]' : '';
    const pasta = it.pasta_type ? `\n   ${escape(it.pasta_type)}` : '';
    const sups = (it.supplements || []).map((s) =>
      `\n   + ${escape(s.name)} ${s.price === 0 ? '(gratuit)' : `(+${eur(s.price)})`}`
    ).join('');
    return `
<div class="line">
  <div class="row">
    <span class="qty">${it.quantity}x ${escape(it.name)}${giant}</span>
    <span class="price">${eur(linePrice)}</span>
  </div>
  ${pasta || sups ? `<div class="sub">${pasta}${sups}</div>` : ''}
</div>`;
  }).join('');

  const orderTypeLabel = order.order_type === 'livraison' ? 'LIVRAISON' : 'A EMPORTER';
  const paymentLabel = order.payment_method === 'cash'
    ? 'PAIEMENT ESPECES'
    : `PAIEMENT CARTE${order.payment_status === 'paid' ? ' (PAYE)' : ''}`;
  const desiredTime = order.desired_time
    ? `${order.order_date || ''} - ${order.desired_time}`
    : 'Au plus vite';

  return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>Ticket #${escape((order.id || '').slice(0, 8))}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: 'Courier New', Courier, monospace;
    color: #000;
    background: #fff;
  }
  body {
    width: 72mm;
    padding: 4mm 3mm 6mm 3mm;
    font-size: 11pt;
    line-height: 1.25;
  }
  .center { text-align: center; }
  .bold { font-weight: 700; }
  .big { font-size: 14pt; }
  .xl { font-size: 16pt; }
  .sep {
    border-top: 1px dashed #000;
    margin: 6px 0;
  }
  .row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
  .qty { flex: 1; }
  .price { white-space: nowrap; }
  .sub {
    font-size: 9pt;
    white-space: pre-line;
    margin-left: 2mm;
  }
  .line { margin: 2px 0; }
  .badge {
    display: inline-block;
    border: 1.5px solid #000;
    padding: 2px 6px;
    font-weight: 700;
    margin: 4px 0;
  }
  .small { font-size: 9pt; }
  .notes {
    border: 1.5px dashed #000;
    padding: 4px 6px;
    margin: 4px 0;
    font-size: 10pt;
  }
  .total-row { font-size: 13pt; font-weight: 700; }
  @media screen {
    body { box-shadow: 0 0 12px rgba(0,0,0,0.15); margin: 20px auto; }
  }
</style>
</head>
<body>
  <div class="center bold xl">${escape(RESTAURANT.name)}</div>
  <div class="center small">${escape(RESTAURANT.address)}<br>${escape(RESTAURANT.city)}<br>Tel ${escape(RESTAURANT.phone)}</div>

  <div class="sep"></div>

  <div class="center"><span class="badge">${orderTypeLabel}</span></div>

  <div class="row small">
    <span>Ticket #${escape((order.id || '').slice(0, 8))}</span>
    <span>${formatDateTime(order.created_at)}</span>
  </div>

  <div class="sep"></div>

  <div class="bold">${escape(order.customer_name || '')}</div>
  <div class="small">${escape(order.customer_phone || '')}</div>
  ${order.customer_email ? `<div class="small">${escape(order.customer_email)}</div>` : ''}
  ${order.delivery_address ? `<div class="small bold">${order.delivery_postal_code ? escape(order.delivery_postal_code) + ' — ' : ''}${escape(order.delivery_address)}</div>` : ''}

  <div class="sep"></div>

  <div class="bold">${escape(desiredTime)}</div>

  <div class="sep"></div>

  ${itemsHTML}

  <div class="sep"></div>

  ${order.notes ? `<div class="notes"><span class="bold">NOTE :</span><br>${escape(order.notes)}</div>` : ''}

  <div class="row"><span>Sous-total</span><span>${eur(subtotal)}</span></div>
  ${discount > 0 ? `<div class="row"><span>Reduction${order.promo_meta?.code ? ` (${escape(order.promo_meta.code)})` : ''}</span><span>-${eur(discount)}</span></div>` : ''}
  <div class="row total-row"><span>TOTAL</span><span>${eur(total)}</span></div>

  <div class="sep"></div>

  <div class="center bold">${paymentLabel}</div>

  <div class="sep"></div>

  <div class="center small">Merci pour votre commande !<br>A bientot chez NotaBene Sympho</div>
</body>
</html>`;
};

/**
 * Open a new window with the ticket and trigger the system print dialog.
 * On tablets, users can then pick their Bluetooth thermal printer.
 */
export const printOrderTicket = (order) => {
  if (!order) return;
  const w = window.open('', '_blank', 'width=420,height=720');
  if (!w) {
    alert("Veuillez autoriser les pop-ups pour imprimer le ticket.");
    return;
  }
  w.document.write(buildTicketHTML(order));
  w.document.close();
  w.focus();
  // Wait a moment for fonts/layout to settle, then trigger the print dialog.
  setTimeout(() => {
    try { w.print(); } catch (e) { /* ignore */ }
  }, 250);
};

export default printOrderTicket;
